// Kinio KMS 操作台的 API client（SPEC #kinio-kms-api；kmgr 的對外路由都在 plane api 的 /v1/…/kmgr/…，Caddy 轉 kmgr）。
//
// token：kmgr 只收 aud = kinio-kmgr 的 token（≤ 15 分鐘）。VMS 經自己的 BFF `POST /session/kmgr-token`
// （rust/src/bff.rs → identity POST /v1/auth/kmgr-token）用目前的登入 token 換；快取到過期前 60 秒，
// 登入 token 換了（refresh、重新驗證）就重換。
//   - 401 → 丟掉快取重換一次再送；還是 401 就拋（不清 VMS 的登入，kmgr 的 401 不代表 VMS session 失效）
//   - 403 REAUTH_REQUIRED（建權杖、撤銷、改輪值表要 step-up）→ 跳重新驗證對話框 → 用新的登入 token 重換 kmgr token → 重送一次
// mock：mockMode.ts 打開時，MISSING_ENDPOINTS 裡的端點（kmgr 還沒有的）改由 mock.ts 回；`all` 連已有的也 mock（kmgr 沒部署時看畫面）。
// 回應形狀：kmgr 原始形狀在 wire.ts，這裡轉成畫面形狀（types.ts）再交出去；mock 也回原始形狀、走同一個轉換。
import { errorFromResponse, KinioError, planeBase, refresh, requestStepUp, sdkHeaders, session } from '@kinio/web-sdk';
import type { KmgrMockMode } from './mockMode';
import { kmgrMock, type MockWorld } from './mock';
import { isRemoteMockPath, remoteMock } from './remote/mock';
import { isTemplateMockPath, templateMock } from './templates/mock';
import type { EnrollReview, EnrollReviewResult, KeysetChange, KeysetChangeResult, KeysetView } from './types';
import type {
  BusinessHours,
  CreateDeployTokenInput,
  CreatedDeployToken,
  DeployToken,
  EnableResponse,
  Enrollment,
  HomeKmgrStatus,
  HomeNotify,
  HostList,
  HostRow,
  Incident,
  IncidentList,
  KmgrDashboard,
  Maintenance,
  MaintenanceInput,
  OncallSchedule,
  OncallScheduleInput,
  OrgKmgrStatus,
  OrgSeats,
  Probe,
  ProbeInput,
  ProbeTestResult,
  PutTargetInput,
  Seat,
  SecurityPolicy,
  SiteInfo,
  Thresholds,
  TimeWindows,
} from './types';
import type { StateRequest } from './model';
import {
  adaptBusinessHours,
  adaptDashboard,
  adaptHomeKmgr,
  adaptHostList,
  adaptIncidentDetail,
  adaptIncidentList,
  adaptMaintenance,
  adaptOrgKmgr,
  adaptOrgSeats,
  adaptProbes,
  adaptSiteInfo,
  adaptStateResponse,
  adaptTargetHosts,
  adaptThresholds,
  type LocalNames,
  type WireBusinessHours,
  type WireDashboard,
  type WireHomeKmgr,
  type WireHostList,
  type WireIncidentDetail,
  type WireIncidentList,
  type WireMaintenance,
  type WireOrgKmgr,
  type WireOrgSeats,
  type WireSiteInfo,
  type WireStateResponse,
  type WireTargetList,
  type WireThresholds,
} from './wire';

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

/**
 * kmgr 還沒有、畫面又要用的端點（mock 模式 `missing` 只接這些）；kmgr 做好再拿掉。
 * 2026-10-16 M2-13b：門檻與等級、現場資訊 kmgr 0.16.0 都有了，清單又是空的（機制留著）。
 */
export const MISSING_ENDPOINTS: { method: Method; re: RegExp }[] = [];

export function isMissingEndpoint(method: Method, path: string): boolean {
  return MISSING_ENDPOINTS.some((e) => e.method === method && e.re.test(path));
}

// ---- 執行環境（Shell 每次 render 設；測試可直接設） ----

let mode: KmgrMockMode = 'off';
let world: MockWorld = { me: '', orgs: [], homes: [] };

export function configureKmgr(m: KmgrMockMode, w?: MockWorld): void {
  mode = m;
  if (w) world = w;
}

export function kmgrMode(): KmgrMockMode {
  return mode;
}

/** 畫面自己知道的家戶與組織名稱（kmgr 的 names 查不到時用；= 範圍切換器的資料）。 */
function localNames(): LocalNames {
  return {
    homes: Object.fromEntries(world.homes.map((h) => [h.home_id, h.display_name])),
    orgs: Object.fromEntries(world.orgs.map((o) => [o.org_id, o.display_name])),
  };
}

// ---- kmgr token ----

interface CachedToken {
  token: string;
  exp: number;
  /** 換這張時用的登入 token（登入 token 換了就重換） */
  src: string;
}

let cached: CachedToken | null = null;
let minting: Promise<CachedToken> | null = null;

export function dropKmgrToken(): void {
  cached = null;
}

async function mint(src: string, retried = false): Promise<CachedToken> {
  let res: Response;
  try {
    res = await fetch('/session/kmgr-token', { method: 'POST', headers: { 'X-Kinio-CSRF': '1', Authorization: 'Bearer ' + src }, credentials: 'same-origin' });
  } catch (e) {
    throw KinioError.network(e);
  }
  if (res.status === 401 && !retried) {
    // 登入 token 過期：照 SDK 的 refresh 換一張再換 kmgr token（refresh 失敗由 SDK 處理登出）
    const fresh = await refresh();
    return mint(fresh, true);
  }
  if (!res.ok) throw await errorFromResponse(res);
  const body = (await res.json()) as { access_token?: string; expires_in?: number; expires_at?: string };
  if (!body.access_token) throw new KinioError(502, 'BAD_KMGR_TOKEN', 'no access_token');
  const exp = body.expires_at ? Date.parse(body.expires_at) : Date.now() + (body.expires_in ?? 600) * 1000;
  return { token: body.access_token, exp, src: session.accessToken() ?? src };
}

/** 目前可用的 kmgr token（同時多個請求共用一次換發）。 */
export async function kmgrToken(): Promise<string> {
  const src = session.accessToken();
  if (!src) throw new KinioError(401, 'NO_SESSION', 'not signed in');
  if (cached && cached.src === src && cached.exp - 60_000 > Date.now()) return cached.token;
  if (!minting)
    minting = mint(src).finally(() => {
      minting = null;
    });
  cached = await minting;
  return cached.token;
}

// ---- 送請求 ----

interface Opts {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | string[] | undefined>;
}

function qs(q: Opts['query']): string {
  if (!q) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (Array.isArray(v)) v.forEach((x) => p.append(k, x)); // scope 可重複帶（組織多選，M5-04）
    else if (v !== undefined && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? '?' + s : '';
}

export async function kmgrFetch<T>(api: string, path: string, opts: Opts = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  if (mode === 'all' || (mode === 'missing' && isMissingEndpoint(method, path))) {
    // 遠端協助（M4-06）的 mock 另一份（remote/mock.ts）；主機沿用這裡的假主機
    if (isRemoteMockPath(path)) return remoteMock.handle(world, method, path, opts.query ?? {}, opts.body) as Promise<T>;
    // 監控範本（M5-02）的 mock 另一份（templates/mock.ts）
    if (isTemplateMockPath(path)) return templateMock.handle(world, method, path, opts.query ?? {}, opts.body) as Promise<T>;
    return kmgrMock.handle(world, method, path, opts.query ?? {}, opts.body) as Promise<T>;
  }
  return send<T>(api, path + qs(opts.query), method, opts.body, false, false);
}

async function send<T>(api: string, pathQ: string, method: Method, body: unknown, retried: boolean, steppedUp: boolean): Promise<T> {
  const token = await kmgrToken();
  const headers: Record<string, string> = { Authorization: 'Bearer ' + token, ...sdkHeaders() };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(planeBase(api) + pathQ, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch (e) {
    throw KinioError.network(e);
  }
  if (res.status === 401 && !retried) {
    dropKmgrToken();
    return send<T>(api, pathQ, method, body, true, steppedUp);
  }
  if (!res.ok) {
    const err = await errorFromResponse(res);
    if (res.status === 403 && err.code === 'REAUTH_REQUIRED' && !steppedUp && (await requestStepUp())) {
      dropKmgrToken();
      return send<T>(api, pathQ, method, body, retried, true);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

// ---- 端點 ----

const enc = encodeURIComponent;
const home = (h: string) => `/v1/homes/${enc(h)}/kmgr`;
const org = (o: string) => `/v1/orgs/${enc(o)}/kmgr`;

export const kmgrApi = {
  dashboard: async (api: string, scope: string | string[]): Promise<KmgrDashboard> =>
    adaptDashboard(await kmgrFetch<WireDashboard>(api, '/v1/me/kmgr/dashboard', { query: { scope } }), localNames()),
  /** claimed_by：user_id 或 none（沒人認領）；handling 沒帶 = 還沒結案的三種。 */
  incidents: async (api: string, q: { scope: string | string[]; now?: string; handling?: string; claimed_by?: string; severity?: string; q?: string; cursor?: string; limit?: number }): Promise<IncidentList> =>
    adaptIncidentList(await kmgrFetch<WireIncidentList>(api, '/v1/me/kmgr/incidents', { query: q }), localNames()),
  incident: async (api: string, homeId: string, incidentId: string): Promise<Incident> =>
    adaptIncidentDetail(await kmgrFetch<WireIncidentDetail>(api, `${home(homeId)}/incidents/${enc(incidentId)}`), localNames()),
  /** 回處理後的事故（沒有詳細與名稱：呼叫端疊在畫面上那一份、再重抓）。 */
  setState: async (api: string, homeId: string, incidentId: string, body: StateRequest): Promise<Incident> =>
    adaptStateResponse(await kmgrFetch<WireStateResponse>(api, `${home(homeId)}/incidents/${enc(incidentId)}/state`, { method: 'POST', body })),

  orgStatus: async (api: string, orgId: string): Promise<OrgKmgrStatus> => adaptOrgKmgr(await kmgrFetch<WireOrgKmgr>(api, org(orgId))),
  enable: (api: string, orgId: string, body: { dpa_version?: string; home_ids: string[] }) => kmgrFetch<EnableResponse>(api, `${org(orgId)}/enable`, { method: 'POST', body }),

  homeStatus: async (api: string, homeId: string): Promise<HomeKmgrStatus> => adaptHomeKmgr(await kmgrFetch<WireHomeKmgr>(api, home(homeId))),
  notify: (api: string, homeId: string) => kmgrFetch<HomeNotify>(api, `${home(homeId)}/notify`),
  setNotify: (api: string, homeId: string, body: { schedule_id: string | null; customer_contact: string | null }) =>
    kmgrFetch<HomeNotify>(api, `${home(homeId)}/notify`, { method: 'PUT', body }),

  deployTokens: (api: string, homeId: string, cursor?: string) =>
    kmgrFetch<{ deploy_tokens: DeployToken[]; next_cursor?: string }>(api, `${home(homeId)}/deploy-tokens`, { query: { cursor } }),
  deployToken: (api: string, homeId: string, tokenId: string) => kmgrFetch<DeployToken>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}`),
  createDeployToken: (api: string, homeId: string, body: CreateDeployTokenInput) =>
    kmgrFetch<CreatedDeployToken>(api, `${home(homeId)}/deploy-tokens`, { method: 'POST', body }),
  revokeDeployToken: (api: string, homeId: string, tokenId: string) => kmgrFetch<DeployToken>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}`, { method: 'DELETE' }),
  /** 〔重建…〕（M5-03）：沿用原設定、綁目前清單、撤銷舊的；step-up。max_count 空 = 還沒報到的台數。 */
  rebuildDeployToken: (api: string, homeId: string, tokenId: string, body: { max_count?: number; expires_at?: string } = {}) =>
    kmgrFetch<CreatedDeployToken>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}/rebuild`, { method: 'POST', body }),
  enrollments: (api: string, homeId: string, tokenId: string, cursor?: string) =>
    kmgrFetch<{ enrollments: Enrollment[]; next_cursor?: string }>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}/enrollments`, { query: { cursor, limit: 200 } }),
  /** 預期清單外的報到（待確認，M5-05）：state 空 = 全部。 */
  enrollReviews: (api: string, homeId: string, tokenId: string, state?: EnrollReview['state']) =>
    kmgrFetch<{ reviews: EnrollReview[] }>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}/reviews`, { query: { state } }),
  /** 接受（要 step-up，send 處理 403 REAUTH_REQUIRED）/ 拒絕（不要）；逐筆回結果。 */
  decideEnrollReviews: (api: string, homeId: string, tokenId: string, decision: 'accept' | 'reject', reviewIds: string[]) =>
    kmgrFetch<{ results: EnrollReviewResult[] }>(api, `${home(homeId)}/deploy-tokens/${enc(tokenId)}/reviews`, { method: 'POST', body: { decision, review_ids: reviewIds } }),

  // ---- M2：主機、網路、席次、時段、門檻、現場資訊 ----

  /** 跨組織 / 跨家戶的主機清單（state：online / offline / expected_offline / unlicensed / suspended / revoked）。 */
  hosts: async (api: string, q: { scope: string | string[]; state?: string; cursor?: string; limit?: number }): Promise<HostList> =>
    adaptHostList(await kmgrFetch<WireHostList>(api, '/v1/me/kmgr/hosts', { query: q }), localNames()),
  /** 一個家戶的主機（targets kind = agent；收集器帶 collector）。 */
  homeHosts: async (api: string, homeId: string): Promise<HostRow[]> =>
    adaptTargetHosts(await kmgrFetch<WireTargetList>(api, `${home(homeId)}/targets`, { query: { kind: 'agent', limit: 200 } }), homeId, localNames()),
  /** 一個家戶的探針（網路設備 = ping / snmp；應用探針 = http / tcp / file）。 */
  probes: async (api: string, homeId: string): Promise<Probe[]> => adaptProbes(await kmgrFetch<WireTargetList>(api, `${home(homeId)}/targets`, { query: { kind: 'probe', limit: 200 } }), homeId),
  putTarget: (api: string, homeId: string, targetRef: string, body: PutTargetInput) => kmgrFetch<unknown>(api, `${home(homeId)}/targets/${enc(targetRef)}`, { method: 'PUT', body }),
  createProbe: (api: string, homeId: string, body: ProbeInput) => kmgrFetch<Probe>(api, `${home(homeId)}/probes`, { method: 'POST', body }),
  putProbe: (api: string, homeId: string, probeId: string, body: ProbeInput) => kmgrFetch<Probe>(api, `${home(homeId)}/probes/${enc(probeId)}`, { method: 'PUT', body }),
  deleteProbe: (api: string, homeId: string, probeId: string) => kmgrFetch<void>(api, `${home(homeId)}/probes/${enc(probeId)}`, { method: 'DELETE' }),
  testProbe: (api: string, homeId: string, body: ProbeInput) => kmgrFetch<ProbeTestResult>(api, `${home(homeId)}/probes/test`, { method: 'POST', body }),
  /** 收集器負責的網段 / 網路設備（整份取代）。 */
  assignCollector: (api: string, homeId: string, device: string, targets: string[]) =>
    kmgrFetch<{ collector: string; assignments: { target: string; target_kind: string }[] }>(api, `${home(homeId)}/collectors/${enc(device)}/assignments`, { method: 'POST', body: { targets } }),

  maintenance: async (api: string, homeId: string): Promise<Maintenance> => adaptMaintenance(await kmgrFetch<WireMaintenance>(api, `${home(homeId)}/maintenance`)),
  /** 新設或延長要 step-up（403 REAUTH_REQUIRED，send 會處理）；清掉不用。 */
  setMaintenance: async (api: string, homeId: string, body: MaintenanceInput): Promise<Maintenance> => adaptMaintenance(await kmgrFetch<WireMaintenance>(api, `${home(homeId)}/maintenance`, { method: 'PUT', body })),
  businessHours: async (api: string, homeId: string): Promise<BusinessHours> => adaptBusinessHours(await kmgrFetch<WireBusinessHours>(api, `${home(homeId)}/business-hours`)),
  setBusinessHours: async (api: string, homeId: string, body: TimeWindows): Promise<BusinessHours> =>
    adaptBusinessHours(await kmgrFetch<WireBusinessHours>(api, `${home(homeId)}/business-hours`, { method: 'PUT', body })),

  seats: async (api: string, orgId: string): Promise<OrgSeats> => adaptOrgSeats(await kmgrFetch<WireOrgSeats>(api, `${org(orgId)}/seats`), localNames()),
  /** 改派（assigned_ref；"" = 解除）/ 改類別（class）。 */
  putSeat: async (api: string, orgId: string, seatId: string, body: { assigned_ref?: string; class?: Seat['class'] }): Promise<Seat> =>
    (await kmgrFetch<{ seat: Seat }>(api, `${org(orgId)}/seats/${enc(seatId)}`, { method: 'PUT', body })).seat,
  securityPolicy: (api: string, orgId: string) => kmgrFetch<SecurityPolicy>(api, `${org(orgId)}/security-policy`),

  /** 門檻與等級（kmgr M2-13b）：PUT 整筆覆寫；防抖只能比預設長、影響錄影的不能關（422 INVALID_ARGUMENT detail.field）。 */
  thresholds: async (api: string, homeId: string): Promise<Thresholds> => adaptThresholds(await kmgrFetch<WireThresholds>(api, `${home(homeId)}/severity-overrides`)),
  setThresholds: async (api: string, homeId: string, body: Pick<Thresholds, 'rules' | 'disk_pct' | 'tls_days' | 'workstation_offline_days'>): Promise<Thresholds> =>
    adaptThresholds(await kmgrFetch<WireThresholds>(api, `${home(homeId)}/severity-overrides`, { method: 'PUT', body })),
  /** 現場資訊（kmgr M2-13b）：整筆覆寫，沒帶的欄位 = 清掉。 */
  siteInfo: async (api: string, homeId: string): Promise<SiteInfo> => adaptSiteInfo(await kmgrFetch<WireSiteInfo>(api, `${home(homeId)}/site-info`)),
  setSiteInfo: async (api: string, homeId: string, body: Omit<SiteInfo, 'home_id' | 'updated_by' | 'updated_at'>): Promise<SiteInfo> =>
    adaptSiteInfo(await kmgrFetch<WireSiteInfo>(api, `${home(homeId)}/site-info`, { method: 'PUT', body })),

  schedules: async (api: string, orgId: string): Promise<OncallSchedule[]> =>
    (await kmgrFetch<{ org_id: string; schedules: OncallSchedule[] }>(api, `${org(orgId)}/oncall-schedules`))?.schedules ?? [],
  createSchedule: (api: string, orgId: string, body: OncallScheduleInput) => kmgrFetch<OncallSchedule>(api, `${org(orgId)}/oncall-schedules`, { method: 'POST', body }),
  /** PUT 必帶 expected_version（版本不符 409 CONFLICT）。 */
  putSchedule: (api: string, orgId: string, id: string, body: OncallScheduleInput & { expected_version: number }) =>
    kmgrFetch<OncallSchedule>(api, `${org(orgId)}/oncall-schedules/${enc(id)}`, { method: 'PUT', body }),

  /** Agent 管理員公鑰清單（M3-05；組織 owner / org_admin）。 */
  keysets: (api: string, orgId: string) => kmgrFetch<KeysetView>(api, `${org(orgId)}/keysets`),
  /** 新增（要簽章頁的斷言；目前清單是空的不用）/ 移除（要原因；組織之前的權杖失效）。dryRun 只回差異。要 step-up。 */
  changeKeyset: (api: string, orgId: string, body: KeysetChange, dryRun = false) =>
    kmgrFetch<KeysetChangeResult>(api, `${org(orgId)}/keysets`, { method: 'POST', body, query: dryRun ? { dry_run: '1' } : undefined }),
};
